import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  ArrowLeft, CheckCircle, XCircle, AlertTriangle, User, Shield, Mail, Phone, MapPin, Briefcase, FileWarning
} from 'lucide-react';
import { toast } from 'sonner';

const TalentApproval = () => {
  const navigate = useNavigate();
  const [talents, setTalents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [page, setPage] = useState(1);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchTalents();
  }, [statusFilter, page]);

  const fetchTalents = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/talent/pending-approval', {
        params: { status: statusFilter, page, limit: 20 }
      });
      setTalents(res.data.talent);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load talent data');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedTalent || !actionType) return;
    if ((actionType === 'reject' || actionType === 'request-info') && !note.trim()) {
      toast.error('Please provide a reason or note');
      return;
    }
    setProcessing(true);
    try {
      if (actionType === 'approve') {
        await api.post(`/admin/talent/${selectedTalent.user_id}/approve`);
        toast.success('Talent approved');
      } else if (actionType === 'reject') {
        await api.post(`/admin/talent/${selectedTalent.user_id}/reject`, null, { params: { reason: note } });
        toast.success('Talent rejected');
      } else if (actionType === 'request-info') {
        await api.post(`/admin/talent/${selectedTalent.user_id}/request-info`, null, { params: { note } });
        toast.success('Information requested');
      } else if (actionType === 'add-note') {
        await api.post(`/admin/talent/${selectedTalent.user_id}/add-note`, null, { params: { note } });
        toast.success('Note added');
      }
      setSelectedTalent(null);
      setActionType(null);
      setNote('');
      fetchTalents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_review: { cls: 'bg-amber-100 text-amber-700', label: 'Pending Review' },
      approved: { cls: 'bg-green-100 text-green-700', label: 'Approved' },
      rejected: { cls: 'bg-red-100 text-red-700', label: 'Rejected' },
      incomplete: { cls: 'bg-orange-100 text-orange-700', label: 'Incomplete' }
    };
    const c = config[status] || config.pending_review;
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-approval-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Talent Approval</h1>
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500">{total} total</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : talents.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No talent profiles with status: {statusFilter}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {talents.map((t) => (
              <Card key={t.user_id} className="bg-white border-gray-200 hover:shadow-md transition-shadow" data-testid="talent-approval-card">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-14 h-14 border-2 border-gray-200">
                      <AvatarImage src={t.profile_photo_url} />
                      <AvatarFallback className="bg-gray-100 text-gray-500">
                        <User className="w-6 h-6" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-900">{t.first_name} {t.last_name}</h3>
                        {getStatusBadge(t.approval_status)}
                        {t.idv_status === 'approved' && (
                          <Badge className="bg-blue-100 text-blue-700"><Shield className="w-3 h-3 mr-1" />ID Verified</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{t.email}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.city}, {t.state}</span>
                        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{t.role_name || 'No role'}</span>
                        <span className="text-xs">Profile: {t.profile_completion_pct}% | Gigs: {t.completed_gigs}</span>
                      </div>
                      {t.approval_notes && (
                        <p className="mt-2 text-xs text-gray-400 bg-gray-50 rounded p-2 whitespace-pre-wrap">{t.approval_notes}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      {t.approval_status !== 'approved' && (
                        <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white"
                          onClick={() => { setSelectedTalent(t); setActionType('approve'); setNote(''); }}
                          data-testid="approve-talent-btn">
                          <CheckCircle className="w-3 h-3 mr-1" />Approve
                        </Button>
                      )}
                      {t.approval_status !== 'rejected' && (
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                          onClick={() => { setSelectedTalent(t); setActionType('reject'); setNote(''); }}
                          data-testid="reject-talent-btn">
                          <XCircle className="w-3 h-3 mr-1" />Reject
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-orange-600 border-orange-200"
                        onClick={() => { setSelectedTalent(t); setActionType('request-info'); setNote(''); }}
                        data-testid="request-info-btn">
                        <FileWarning className="w-3 h-3 mr-1" />Request Info
                      </Button>
                      <Button size="sm" variant="ghost" className="text-gray-500"
                        onClick={() => { setSelectedTalent(t); setActionType('add-note'); setNote(''); }}
                        data-testid="add-note-btn">
                        Add Note
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Action Dialog */}
      <Dialog open={!!actionType && !!selectedTalent} onOpenChange={() => { setActionType(null); setSelectedTalent(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Talent'}
              {actionType === 'reject' && 'Reject Talent'}
              {actionType === 'request-info' && 'Request More Information'}
              {actionType === 'add-note' && 'Add Admin Note'}
            </DialogTitle>
            <DialogDescription>
              {selectedTalent?.first_name} {selectedTalent?.last_name} ({selectedTalent?.email})
            </DialogDescription>
          </DialogHeader>
          {actionType === 'approve' ? (
            <p className="text-sm text-gray-600 py-4">
              This will approve the talent profile and allow them to apply for gigs. They will be notified.
            </p>
          ) : (
            <div className="py-4">
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  actionType === 'reject' ? 'Reason for rejection...' :
                  actionType === 'request-info' ? 'What information is needed...' :
                  'Add a note...'
                }
                rows={4}
                data-testid="action-note-textarea"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionType(null); setSelectedTalent(null); }}>Cancel</Button>
            <Button
              className={
                actionType === 'approve' ? 'bg-green-500 hover:bg-green-600 text-white' :
                actionType === 'reject' ? 'bg-red-500 hover:bg-red-600 text-white' :
                'bg-cyan-500 hover:bg-cyan-600 text-white'
              }
              onClick={handleAction}
              disabled={processing}
              data-testid="confirm-action-btn"
            >
              {processing ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentApproval;
